import React from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface FeeCollectionSchoolERPProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const FeeCollectionSchoolERP: React.FC<FeeCollectionSchoolERPProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  const features = [
    { icon: 'bi-credit-card-2-front', title: 'Online Payment Gateway', desc: 'Accept payments via UPI, cards, net banking & wallets through Razorpay', color: '#28A745' },
    { icon: 'bi-receipt', title: 'Automated Invoicing', desc: 'Generate and send fee invoices automatically to parents', color: '#2D7DD2' },
    { icon: 'bi-bell', title: 'Payment Reminders', desc: 'WhatsApp & SMS reminders for pending and overdue fees', color: '#FFC107' },
    { icon: 'bi-calendar3', title: 'Installment Plans', desc: 'Flexible payment schedules with custom due dates', color: '#5C2597' },
    { icon: 'bi-graph-up', title: 'Collection Reports', desc: 'Real-time dashboards showing collected, pending & overdue amounts', color: '#E91E63' },
    { icon: 'bi-shield-check', title: 'Secure Transactions', desc: 'PCI-DSS compliant payment processing with encryption', color: '#00BCD4' },
  ];

  const paymentMethods = [
    { name: 'UPI', icon: 'bi-phone' },
    { name: 'Credit Card', icon: 'bi-credit-card' },
    { name: 'Debit Card', icon: 'bi-credit-card-2-back' },
    { name: 'Net Banking', icon: 'bi-bank' },
    { name: 'Wallets', icon: 'bi-wallet2' },
    { name: 'Cash/Cheque', icon: 'bi-cash-stack' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #28A745 0%, #20c997 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-7">
              <span className="badge bg-white text-success mb-3 px-3 py-2">
                <i className="bi bi-currency-rupee me-2"></i>Fee Management Solution
              </span>
              <h1 className="display-4 fw-bold mb-4">
                Simplify School Fee Collection with Smart ERP
              </h1>
              <p className="lead mb-4 opacity-90">
                Collect fees online, track payments, send automated reminders, and generate
                detailed reports. Reduce fee defaults by up to 40% with our intelligent fee management system.
              </p>
              <div className="d-flex gap-3 flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Book Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4">
                  <i className="bi bi-download me-2"></i>Download Brochure
                </button>
              </div>
            </div>
            <div className="col-lg-5 text-center mt-4 mt-lg-0">
              <div className="bg-white rounded-4 p-4 shadow-lg">
                <h5 className="text-dark mb-4">Supported Payment Methods</h5>
                <div className="row g-3">
                  {paymentMethods.map((method, idx) => (
                    <div key={idx} className="col-4">
                      <div className="text-center p-2">
                        <i className={`bi ${method.icon} fs-3 text-success`}></i>
                        <p className="small text-muted mb-0 mt-1">{method.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Complete Fee Management Features</h2>
            <p className="text-muted lead">Everything you need to streamline fee collection</p>
          </div>
          <div className="row g-4">
            {features.map((feature, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${feature.color}15` }}>
                      <i className={`bi ${feature.icon} fs-4`} style={{ color: feature.color }}></i>
                    </div>
                    <h5 className="card-title">{feature.title}</h5>
                    <p className="card-text text-muted">{feature.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="row text-center">
            <div className="col-6 col-md-3 mb-4 mb-md-0">
              <h2 className="fw-bold text-success">98%</h2>
              <p className="text-muted mb-0">Collection Rate</p>
            </div>
            <div className="col-6 col-md-3 mb-4 mb-md-0">
              <h2 className="fw-bold text-success">40%</h2>
              <p className="text-muted mb-0">Reduced Defaults</p>
            </div>
            <div className="col-6 col-md-3">
              <h2 className="fw-bold text-success">5 Min</h2>
              <p className="text-muted mb-0">Payment Time</p>
            </div>
            <div className="col-6 col-md-3">
              <h2 className="fw-bold text-success">24/7</h2>
              <p className="text-muted mb-0">Payment Portal</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">How Fee Collection Works</h2>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="row g-4">
                {[
                  { step: '1', title: 'Set Fee Structure', desc: 'Define fee components, amounts, and due dates' },
                  { step: '2', title: 'Generate Invoices', desc: 'Auto-generate and send invoices to parents' },
                  { step: '3', title: 'Parents Pay Online', desc: 'Parents pay via UPI, cards, or bank transfer' },
                  { step: '4', title: 'Auto Reconciliation', desc: 'Payments automatically reconciled and receipts sent' },
                ].map((item, idx) => (
                  <div key={idx} className="col-md-3">
                    <div className="text-center">
                      <div className="rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center mb-3"
                        style={{ width: '50px', height: '50px' }}>
                        <strong>{item.step}</strong>
                      </div>
                      <h6>{item.title}</h6>
                      <p className="small text-muted">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Start Collecting Fees Online Today</h2>
          <p className="lead opacity-75 mb-4">Join schools that have improved their fee collection by 40%</p>
          <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
            <i className="bi bi-telephone me-2"></i>Talk to Sales
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FeeCollectionSchoolERP;
